import { useState, useEffect, useRef, type FormEvent } from 'react';
import ScrollReveal from '@/components/feature/ScrollReveal';
import { sanitizeText } from '@/utils/sanitize';

const SUBMIT_COOLDOWN_MS = 15000;
const RECIPIENT = 'lucky@luckyholidays.in';

function validateIndianMobile(value: string): { valid: boolean; message?: string } {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) {
    return { valid: false, message: 'Mobile number is required.' };
  }
  let core = digits;
  if (core.startsWith('91') && core.length > 10) {
    core = core.slice(2);
  }
  if (core.startsWith('0')) {
    core = core.slice(1);
  }
  if (core.length !== 10) {
    return { valid: false, message: 'Please enter a valid 10-digit mobile number.' };
  }
  const firstDigit = core.charAt(0);
  if (!['6', '7', '8', '9'].includes(firstDigit)) {
    return { valid: false, message: 'Please enter a valid Indian mobile number starting with 6, 7, 8, or 9.' };
  }
  return { valid: true };
}

function escapeMailtoBody(text: string): string {
  // RFC 6068: mailto body encoding — encodeURIComponent then fix %20 → space handling
  // Newlines must be encoded as %0D%0A for maximum client compatibility
  return encodeURIComponent(text).replace(/%20/g, ' ');
}

function buildMailtoUrl(
  recipient: string,
  subject: string,
  body: string
): string {
  const safeSubject = escapeMailtoBody(subject);
  const safeBody = escapeMailtoBody(body);
  return `mailto:${recipient}?subject=${safeSubject}&body=${safeBody}`;
}

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [shakeError, setShakeError] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const lastSubmitRef = useRef(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mailtoAttemptRef = useRef(false);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (status === 'error') {
      setShakeError(true);
      const t = setTimeout(() => setShakeError(false), 500);
      return () => clearTimeout(t);
    }
  }, [status]);

  const startCooldown = () => {
    const now = Date.now();
    lastSubmitRef.current = now;
    setCooldownRemaining(SUBMIT_COOLDOWN_MS);

    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastSubmitRef.current;
      const remaining = Math.max(0, SUBMIT_COOLDOWN_MS - elapsed);
      setCooldownRemaining(remaining);
      if (remaining <= 0 && cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    }, 200);
  };

  const triggerMailto = (url: string): boolean => {
    // Remove any previously created temporary anchor
    const existing = document.getElementById('mailto-trigger');
    if (existing) existing.remove();

    const a = document.createElement('a');
    a.id = 'mailto-trigger';
    a.href = url;
    a.style.position = 'fixed';
    a.style.opacity = '0';
    a.style.pointerEvents = 'none';
    a.style.zIndex = '-1';
    document.body.appendChild(a);

    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    const dispatched = a.dispatchEvent(clickEvent);

    // Clean up after a short delay
    setTimeout(() => {
      const el = document.getElementById('mailto-trigger');
      if (el) el.remove();
    }, 100);

    return dispatched;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Rate-limit check
    const timeSinceLast = Date.now() - lastSubmitRef.current;
    if (timeSinceLast < SUBMIT_COOLDOWN_MS) {
      setStatus('error');
      setErrorMsg(`Please wait ${Math.ceil(cooldownRemaining / 1000)} seconds before submitting again.`);
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Full Name validation
    const rawFullName = formData.get('fullName')?.toString().trim() || '';
    if (!rawFullName) {
      setStatus('error');
      setErrorMsg('Full name is required.');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }

    // Mobile validation
    const rawMobile = formData.get('mobile')?.toString().trim() || '';
    const mobileCheck = validateIndianMobile(rawMobile);
    if (!mobileCheck.valid) {
      setStatus('error');
      setErrorMsg(mobileCheck.message || 'Invalid mobile number.');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }

    // Email validation
    const rawEmail = formData.get('email')?.toString().trim() || '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(rawEmail)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    // Sanitize all inputs before embedding in mailto URL
    const fullName = sanitizeText(rawFullName) || 'Not provided';
    const mobile = sanitizeText(rawMobile) || 'Not provided';
    const email = sanitizeText(rawEmail) || 'Not provided';
    const destination = sanitizeText(formData.get('destination')?.toString().trim() || 'Not specified');
    const travelDate = sanitizeText(formData.get('travelDate')?.toString().trim() || 'Not specified');
    const travellers = sanitizeText(formData.get('travellers')?.toString().trim() || 'Not specified');
    const holidayType = sanitizeText(formData.get('holidayType')?.toString().trim() || 'Not specified');
    const requirements = sanitizeText(formData.get('requirements')?.toString().trim() || 'None');

    const subject = `Travel Inquiry from ${fullName} - Lucky Holidays`;
    const body =
`New Travel Inquiry from Lucky Holidays Website
=====================================

Full Name: ${fullName}
Mobile Number: ${mobile}
Email Address: ${email}
Preferred Destination: ${destination}
Travel Date: ${travelDate}
Number of Travellers: ${travellers}
Holiday Type: ${holidayType}

Special Requirements:
${requirements}

=====================================
Submitted via luckyholidays.in contact form`;

    const mailtoUrl = buildMailtoUrl(RECIPIENT, subject, body);

    mailtoAttemptRef.current = false;

    // Attempt to trigger mailto
    const dispatched = triggerMailto(mailtoUrl);

    // Also set window.location as a fallback in case the anchor click doesn't work
    // (some browsers block programmatic clicks on mailto links)
    if (!dispatched) {
      window.location.href = mailtoUrl;
    }

    // Mark that we attempted
    mailtoAttemptRef.current = true;

    // Show success after a brief delay so the browser has time to hand off to email client
    setTimeout(() => {
      setStatus('success');
      startCooldown();
      // Do NOT reset the form — keep data visible in case mailto failed
    }, 600);
  };

  const inputBaseClasses =
    'w-full pl-10 pr-4 py-3 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 form-input-focus-ring focus:outline-none focus:border-primary-400';

  return (
    <section id="contact" className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-16 3xl:max-w-[1800px] 3xl:mx-auto tv:max-w-[3000px] tv:mx-auto">
        <div className="max-w-3xl tv:max-w-4xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-4 py-1.5 bg-accent-50 text-accent-700 text-xs font-semibold tracking-widest uppercase rounded-full mb-4">
                Get In Touch
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl 3xl:text-6xl font-bold text-foreground-900 mb-4">
                {'Start Planning Your Journey'.split(' ').map((word, i) => (
                  <span
                    key={i}
                    className="inline-block mr-[0.3em] animate-text-reveal"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    {word}
                  </span>
                ))}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed animate-text-reveal" style={{ animationDelay: '0.5s' }}>
                Fill in your details, click submit, and your email app will open with everything pre-filled. Just hit Send — we'll receive it instantly at lucky@luckyholidays.in.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={150}>
            <form
              onSubmit={handleSubmit}
              className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-10 relative card-hover-lift"
            >
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 md:gap-5">
                {/* Full Name */}
                <div className="group">
                  <label htmlFor="fullName" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                    Full Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400"></i>
                    <input
                      id="fullName"
                      required
                      placeholder="Enter your full name"
                      className={inputBaseClasses}
                      name="fullName"
                    />
                  </div>
                </div>

                {/* Mobile Number */}
                <div className="group">
                  <label htmlFor="mobile" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                    Mobile Number <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-smartphone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400"></i>
                    <input
                      id="mobile"
                      required
                      placeholder="+91 98765 43210"
                      className={inputBaseClasses}
                      type="tel"
                      name="mobile"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="group">
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400"></i>
                    <input
                      id="email"
                      required
                      placeholder="your@email.com"
                      className={inputBaseClasses}
                      type="email"
                      name="email"
                    />
                  </div>
                </div>

                {/* Destination */}
                <div className="group">
                  <label htmlFor="destination" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                    Preferred Destination
                  </label>
                  <div className="relative">
                    <i className="ri-map-pin-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400"></i>
                    <input
                      id="destination"
                      placeholder="e.g., Maldives, Switzerland"
                      className={inputBaseClasses}
                      name="destination"
                    />
                  </div>
                </div>

                {/* Travel Date */}
                <div className="group">
                  <label htmlFor="travelDate" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                    Travel Date
                  </label>
                  <div className="relative">
                    <i className="ri-calendar-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400"></i>
                    <input
                      id="travelDate"
                      className={inputBaseClasses}
                      type="date"
                      name="travelDate"
                    />
                  </div>
                </div>

                {/* Travellers */}
                <div className="group">
                  <label htmlFor="travellers" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                    Number of Travellers
                  </label>
                  <div className="relative">
                    <i className="ri-group-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400"></i>
                    <input
                      id="travellers"
                      min={1}
                      placeholder="e.g., 2"
                      className={inputBaseClasses}
                      type="number"
                      name="travellers"
                    />
                  </div>
                </div>

                {/* Holiday Type */}
                <div className="xs:col-span-2 group">
                  <label htmlFor="holidayType" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                    Holiday Type
                  </label>
                  <div className="relative">
                    <i className="ri-suitcase-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400 pointer-events-none"></i>
                    <select
                      id="holidayType"
                      name="holidayType"
                      className={`${inputBaseClasses} cursor-pointer appearance-none`}
                    >
                      <option value="">Select holiday type</option>
                      <option value="International Tour">International Tour</option>
                      <option value="Domestic Tour">Domestic Tour</option>
                      <option value="Honeymoon Package">Honeymoon Package</option>
                      <option value="Family Holiday">Family Holiday</option>
                      <option value="Group Tour">Group Tour</option>
                      <option value="Corporate Travel">Corporate Travel</option>
                      <option value="Cruise Holiday">Cruise Holiday</option>
                      <option value="Luxury Holiday">Luxury Holiday</option>
                      <option value="Weekend Getaway">Weekend Getaway</option>
                      <option value="Customized Package">Customized Package</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none transition-colors duration-300 group-focus-within:text-primary-400"></i>
                  </div>
                </div>
              </div>

              {/* Special Requirements */}
              <div className="mt-4 group">
                <label htmlFor="requirements" className="block text-xs font-medium text-foreground-600 mb-1.5 transition-colors duration-300 group-focus-within:text-primary-500">
                  Special Requirements
                </label>
                <div className="relative">
                  <i className="ri-chat-1-line absolute left-3.5 top-3.5 text-foreground-400 text-sm transition-colors duration-300 group-focus-within:text-primary-400"></i>
                  <textarea
                    id="requirements"
                    name="requirements"
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about your dream holiday — budget, preferences, special occasions..."
                    className="w-full pl-10 pr-4 py-3 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 form-input-focus-ring focus:outline-none focus:border-primary-400 resize-none"
                  ></textarea>
                </div>
                <p className="text-[11px] text-foreground-400 mt-1 text-right">Max 500 characters</p>
              </div>

              {/* Error Message */}
              {status === 'error' && (
                <div
                  className={`mt-5 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-form-slide-down ${shakeError ? 'animate-form-shake' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                    <span>{errorMsg || 'Something went wrong. Please try again.'}</span>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {status === 'success' && (
                <div className="mt-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm animate-form-slide-down">
                  <div className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line animate-form-check-pop"></i>
                    </span>
                    <div>
                      <p className="font-semibold text-green-800 mb-1">Your email app should be open now!</p>
                      <p className="mb-2">Just hit <strong>Send</strong> in your email client and we'll receive your inquiry.</p>
                      <div className="flex items-center gap-2 p-2.5 bg-green-100/60 border border-green-200/60 rounded-lg">
                        <i className="ri-mail-line text-green-600 flex-shrink-0"></i>
                        <span className="text-green-800 font-medium text-xs break-all">If your email client didn't open, email us directly at: <strong>lucky@luckyholidays.in</strong></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                disabled={status === 'submitting' || status === 'success'}
                className="btn-ripple mt-6 w-full xs:w-auto px-10 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 hover-glow-primary disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer relative overflow-hidden"
              >
                {status === 'submitting' ? (
                  <span className="inline-flex items-center gap-3">
                    <span>Opening Email</span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-background-50 rounded-full animate-dot-bounce" style={{ animationDelay: '0s' }}></span>
                      <span className="w-1.5 h-1.5 bg-background-50 rounded-full animate-dot-bounce" style={{ animationDelay: '0.2s' }}></span>
                      <span className="w-1.5 h-1.5 bg-background-50 rounded-full animate-dot-bounce" style={{ animationDelay: '0.4s' }}></span>
                    </span>
                  </span>
                ) : status === 'success' ? (
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-check-double-line text-lg"></i>
                    <span>Email Opened</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 group/btn">
                    <span>Send Inquiry via Email</span>
                    <span className="w-4 h-4 flex items-center justify-center transition-transform duration-300 group-hover/btn:translate-x-1">
                      <i className="ri-mail-send-line"></i>
                    </span>
                  </span>
                )}
              </button>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}